import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useData, save } from "../hooks/useData";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, DataTable, Button, Modal, Field, Input, Textarea, Select, StatusBadge } from "../components/shared";
import { Plus, X, Search, Trash2 } from "lucide-react";
import { num, fmtDate } from "../lib/api";

export default function ProcessIssue() {
  const { data, loading, reload } = useData("/process-issues");
  const { data: orders } = useData("/production-orders");
  const { data: cuttings } = useData("/cutting");
  const { data: jobbers } = useData("/jobbers");
  const { data: accessories } = useData("/accessories");
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [order, setOrder] = useState(null);
  const [accLines, setAccLines] = useState([]);
  const [orderSearch, setOrderSearch] = useState("");
  const [edit, setEdit] = useState(null);

  const openEdit = (r) => setEdit({ ...r });
  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      const j = (jobbers || []).find((x) => x.id === edit.jobber_id);
      await save("put", `/process-issues/${edit.id}`, { ...edit, jobber_name: j?.name ?? edit.jobber_name });
      toast.success("Issue updated"); setEdit(null); reload();
    } catch {}
  };
  const delIssue = async (r) => {
    if (!window.confirm(`Delete issue${r.challan_number ? ` ${r.challan_number}` : ""} for ${r.design_number}? WIP will be restored.`)) return;
    try { await api.delete(`/process-issues/${r.id}`); toast.success("Issue deleted, WIP restored"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const sheetFor = (oid) => (cuttings || []).filter((c) => c.production_order_id === oid).map((c) => c.cutting_sheet_number).filter(Boolean).join(", ") || "—";
  const orderLabel = (o) => `Sheet ${sheetFor(o.id)} · ${o.design_number} · ${o.colour} · ${(o.sizes || []).map((s) => s.size).join("/")}`;

  const blank = { production_order_id: "", from_process: "", to_process: "", mode: "Jobber", jobber_id: "", jobber_name: "", part_name: "", total_qty: "", sizes: [], issue_date: new Date().toISOString().slice(0, 10), issue_rate: "", expected_receiving_date: "", remarks: "", generate_challan: false };
  const openForm = () => { setForm(blank); setOrder(null); setAccLines([]); setOpen(true); };

  const onPickOrder = (id) => {
    const o = (orders || []).find((x) => x.id === id);
    setOrder(o);
    setForm((f) => ({ ...f, production_order_id: id, sizes: o?.sizes || [], from_process: "", to_process: "" }));
  };

  const routeOptions = order?.route || [];
  const onPickFrom = (fp) => {
    const idx = routeOptions.indexOf(fp);
    const nextProc = routeOptions[idx + 1] || "";
    setForm((f) => ({ ...f, from_process: fp, to_process: nextProc }));
  };
  const stageAvail = (name) => {
    const s = (order?.stages || []).find((x) => x.name === name);
    return s ? (s.qty_in || 0) - (s.qty_issued || 0) : 0;
  };

  useEffect(() => {
    if (location.state?.orderId && orders && !open) {
      const o = orders.find((x) => x.id === location.state.orderId);
      if (o) { setForm({ ...blank, production_order_id: o.id, sizes: o.sizes || [], from_process: location.state.from || "", to_process: "" }); setOrder(o); setAccLines([]); setOpen(true); }
    }
    // eslint-disable-next-line
  }, [location.state, orders]);

  const addAccLine = () => setAccLines([...accLines, { accessory_id: "", quantity: "" }]);
  const updAcc = (i, k, v) => setAccLines(accLines.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));

  const submit = async (e) => {
    e.preventDefault();
    const j = (jobbers || []).find((x) => x.id === form.jobber_id);
    const body = { ...form, jobber_name: j?.name || "", issue_rate: form.issue_rate || j?.rate };
    try {
      const issue = await save("post", "/process-issues", body);
      for (const line of accLines) {
        if (line.accessory_id && line.quantity) {
          await api.post("/accessory-issues", {
            accessory_id: line.accessory_id, quantity: parseFloat(line.quantity), issue_id: issue.id,
            challan_number: issue.challan_number, process: issue.to_process,
            production_order_id: issue.production_order_id, design_number: issue.design_number,
            jobber_id: issue.jobber_id, jobber_name: issue.jobber_name, date: form.issue_date,
          });
        }
      }
      toast.success(issue.challan_number ? `Issued — Challan ${issue.challan_number}` : "Issued (in-house)");
      setOpen(false); reload();
      if (issue.challan_number) navigate(`/challans/${issue.challan_number}`);
    } catch {}
  };

  const columns = [
    { key: "created_at", header: "Issue Date", render: (r) => fmtDate(r.issue_date || r.created_at) },
    { key: "challan_number", header: "Challan", mono: true, render: (r) => r.challan_number ? <button className="font-semibold text-blue-800" onClick={(e) => { e.stopPropagation(); navigate(`/challans/${r.challan_number}`); }}>{r.challan_number}</button> : <span className="text-slate-400">In-House</span> },
    { key: "production_code", header: "Prod Order", mono: true },
    { key: "to_process", header: "Process", render: (r) => `${r.from_process} → ${r.to_process}` },
    { key: "mode", header: "Mode", render: (r) => <StatusBadge status={r.mode} /> },
    { key: "jobber_name", header: "Jobber", render: (r) => r.jobber_name || "-" },
    { key: "total_qty", header: "Issue Qty", align: "right", render: (r) => num(r.total_qty) },
    { key: "received_qty", header: "Received", align: "right", render: (r) => num(r.received_qty) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "act", header: "", sortable: false, render: (r) => (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <button title="Edit" onClick={() => openEdit(r)} className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900" data-testid={`issue-edit-${r.id}`}>Edit</button>
        <button title="Delete" onClick={() => delIssue(r)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`issue-delete-${r.id}`}><Trash2 size={15} /></button>
      </div>
    ) },
  ];

  return (
    <div>
      <PageHeader title="Process Issue" subtitle="Send WIP to jobber or in-house. Same production qty flows forward.">
        <Button onClick={openForm} data-testid="add-issue-btn"><Plus size={15} /> New Process Issue</Button>
      </PageHeader>
      {!loading && <DataTable testid="issues-table" columns={columns} rows={data} exportName="process-issues.csv" onRowClick={openEdit} />}

      <Modal open={open} onClose={() => setOpen(false)} title="New Process Issue" wide>
        {form && (
          <form onSubmit={submit} className="grid grid-cols-3 gap-4">
            <Field label="Select Design (search by cutting sheet, design, colour)" required className="col-span-3">
              <div className="relative mb-2">
                <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
                <Input className="pl-8" placeholder="Type to filter…" value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} data-testid="issue-order-search" />
              </div>
              <Select value={form.production_order_id} onChange={(e) => onPickOrder(e.target.value)} required placeholder="Select design"
                options={(orders || []).filter((o) => o.status !== "Finished" && (o.stages || []).some((s) => s.name === "Cutting" && (s.qty_in || 0) > 0)).filter((o) => orderLabel(o).toLowerCase().includes(orderSearch.toLowerCase())).map((o) => ({ value: o.id, label: orderLabel(o) }))} data-testid="issue-order" />
            </Field>
            {order && (
              <>
                <Field label="From Process" required>
                  <Select value={form.from_process} onChange={(e) => onPickFrom(e.target.value)} required placeholder="Stage"
                    options={routeOptions.map((p) => ({ value: p, label: `${p} (avail ${num(stageAvail(p))})` }))} data-testid="issue-from" />
                </Field>
                <Field label="To Process (any stage — free choice)" required>
                  <Select value={form.to_process} onChange={(e) => setForm({ ...form, to_process: e.target.value })} required placeholder="Select next stage"
                    options={routeOptions.filter((p) => p !== form.from_process).map((p) => ({ value: p, label: p }))} data-testid="issue-to" />
                </Field>
                <Field label="Mode"><Select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} options={["Jobber", "In-House"]} data-testid="issue-mode" /></Field>
                {form.mode === "Jobber" && (
                  <Field label="Jobber" required>
                    <Select value={form.jobber_id} onChange={(e) => setForm({ ...form, jobber_id: e.target.value })} required placeholder="Select jobber"
                      options={(jobbers || []).map((j) => ({ value: j.id, label: j.name }))} data-testid="issue-jobber" />
                  </Field>
                )}
                <Field label="Part / Component (optional — full qty per part, no split)">
                  <Input value={form.part_name} onChange={(e) => setForm({ ...form, part_name: e.target.value })} placeholder="e.g. Front / Back / Sleeve" data-testid="issue-part" />
                </Field>
                <Field label="Issue Quantity" required><Input type="number" step="any" value={form.total_qty} onChange={(e) => setForm({ ...form, total_qty: e.target.value })} required data-testid="issue-qty" />
                  {form.from_process && <p className="text-xs text-slate-400 mt-1">Available: {num(stageAvail(form.from_process))}{form.part_name ? " (part — not deducted from WIP)" : ""}</p>}
                </Field>
                <Field label="Issue Rate"><Input type="number" step="any" value={form.issue_rate} onChange={(e) => setForm({ ...form, issue_rate: e.target.value })} /></Field>
                <Field label="Issue Date"><Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></Field>
                <Field label="Expected Return"><Input type="date" value={form.expected_receiving_date} onChange={(e) => setForm({ ...form, expected_receiving_date: e.target.value })} /></Field>
                {form.mode === "In-House" && (
                  <Field label="Generate Challan"><Select value={form.generate_challan ? "1" : "0"} onChange={(e) => setForm({ ...form, generate_challan: e.target.value === "1" })} options={[{ value: "0", label: "No" }, { value: "1", label: "Yes" }]} /></Field>
                )}

                <div className="col-span-3 border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-slate-700">Accessories Issued (optional)</p>
                    <Button type="button" variant="secondary" size="sm" onClick={addAccLine} data-testid="add-acc-line">+ Add Accessory</Button>
                  </div>
                  {accLines.map((line, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2">
                      <Select className="flex-1" value={line.accessory_id} onChange={(e) => updAcc(i, "accessory_id", e.target.value)} placeholder="Select accessory"
                        options={(accessories || []).map((a) => ({ value: a.id, label: `${a.name} (${a.category}) — stock ${num(a.stock)} ${a.unit}` }))} />
                      <Input className="w-32" type="number" placeholder="Qty" value={line.quantity} onChange={(e) => updAcc(i, "quantity", e.target.value)} />
                      <button type="button" onClick={() => setAccLines(accLines.filter((_, idx) => idx !== i))} className="text-red-400"><X size={16} /></button>
                    </div>
                  ))}
                </div>
                <Field label="Remarks" className="col-span-3"><Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
              </>
            )}
            <div className="col-span-3 flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!order} data-testid="issue-save">Issue & Generate Challan</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!edit} onClose={() => setEdit(null)} title="Edit Process Issue">
        {edit && (
          <form onSubmit={submitEdit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 bg-slate-50 rounded-md p-2 text-sm">{edit.production_code} · {edit.design_number} · {edit.from_process} → {edit.to_process}</div>
            <Field label="Jobber">
              <Select value={edit.jobber_id || ""} onChange={(e) => setEdit({ ...edit, jobber_id: e.target.value })} placeholder="Select jobber"
                options={(jobbers || []).map((j) => ({ value: j.id, label: j.name }))} data-testid="issue-edit-jobber" />
            </Field>
            <Field label="Issue Rate"><Input type="number" step="any" value={edit.issue_rate || ""} onChange={(e) => setEdit({ ...edit, issue_rate: e.target.value })} /></Field>
            <Field label="Issue Date"><Input type="date" value={edit.issue_date || ""} onChange={(e) => setEdit({ ...edit, issue_date: e.target.value })} /></Field>
            <Field label="Expected Return"><Input type="date" value={edit.expected_receiving_date || ""} onChange={(e) => setEdit({ ...edit, expected_receiving_date: e.target.value })} /></Field>
            <Field label="Part / Component"><Input value={edit.part_name || ""} onChange={(e) => setEdit({ ...edit, part_name: e.target.value })} /></Field>
            <Field label="Remarks" className="col-span-2"><Textarea value={edit.remarks || ""} onChange={(e) => setEdit({ ...edit, remarks: e.target.value })} /></Field>
            <p className="col-span-2 text-xs text-slate-400">To change quantity or process, delete this issue and create a new one (WIP will be restored).</p>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setEdit(null)}>Cancel</Button>
              <Button type="submit" data-testid="issue-edit-save">Save Changes</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
